import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Copy, Check, RefreshCw, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { API } from "@/utils/constants";

const CopyField = ({ label, value, mono = true, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — select and copy manually");
    }
  };
  return (
    <div>
      {label && <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8d8b8b] mb-1">{label}</p>}
      <div className="flex items-stretch gap-1.5">
        <code className={`flex-1 min-w-0 px-2.5 py-2 rounded-md bg-gray-50 border border-gray-200 text-[#1f2937] break-all ${mono ? 'font-mono text-xs' : 'text-sm'}`} data-testid={testid}>
          {value}
        </code>
        <Button type="button" variant="outline" size="sm" onClick={copy} className="h-auto px-2.5 border-gray-200" data-testid={`${testid}-copy`}>
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
};

export const ApiAccessDialog = ({ open, onClose, authPassword }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [rotating, setRotating] = useState(false);

  const fetchKey = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/external-api-key`, {
        headers: { "X-Auth-Password": authPassword },
      });
      setData(res.data);
    } catch {
      toast.error("Couldn't load the API key");
    } finally {
      setLoading(false);
    }
  }, [authPassword]);

  useEffect(() => {
    if (open) {
      setRevealed(false);
      fetchKey();
    }
  }, [open, fetchKey]);

  const rotate = async () => {
    if (!window.confirm("Generate a NEW key? The current key will stop working immediately and you'll need to update the couples app.")) return;
    setRotating(true);
    try {
      const res = await axios.post(`${API}/admin/external-api-key/rotate`, {}, {
        headers: { "X-Auth-Password": authPassword },
      });
      setData((d) => ({ ...d, api_key: res.data.api_key }));
      setRevealed(true);
      toast.success("New key generated — update the couples app with it");
    } catch {
      toast.error("Failed to rotate the key");
    } finally {
      setRotating(false);
    }
  };

  const maskedKey = data?.api_key
    ? `${data.api_key.slice(0, 10)}${"•".repeat(22)}${data.api_key.slice(-4)}`
    : "";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="api-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#605e5e]" />
            API Access
          </DialogTitle>
          <DialogDescription>
            Read-only key for the couples/WVE app to read rota data (weddings, venues, dates, team, holidays, editing queues).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-[#605e5e]" />
          </div>
        ) : data ? (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8d8b8b]">API Key</p>
                <button
                  onClick={() => setRevealed((r) => !r)}
                  className="flex items-center gap-1 text-[11px] text-[#605e5e] hover:underline"
                  data-testid="api-key-reveal-toggle"
                >
                  {revealed ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  {revealed ? "Hide" : "Reveal"}
                </button>
              </div>
              <CopyField value={revealed ? data.api_key : maskedKey} testid="api-key-value" />
              <p className="text-[11px] text-[#8d8b8b] mt-1.5 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Keep this secret. Paste it into the couples app, not into a browser/website.
              </p>
            </div>

            <CopyField label="Send as HTTP header" value={`${data.header}: ${revealed ? data.api_key : maskedKey}`} testid="api-header-value" />

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8d8b8b] mb-1">Read-only endpoints</p>
              <div className="space-y-1.5">
                {(data.endpoints || []).map((ep) => (
                  <CopyField key={ep} value={`${API.replace(/\/api$/, "")}${ep}`} testid={`api-endpoint-${ep.split("/").pop()}`} />
                ))}
              </div>
              <p className="text-[11px] text-[#8d8b8b] mt-1.5">
                All GET, read-only. Jobs support <code className="font-mono">?year=2024</code> or <code className="font-mono">?date_from=&date_to=</code>.
              </p>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <p className="text-[11px] text-[#8d8b8b]">Compromised or need to reset access?</p>
              <Button
                variant="outline"
                size="sm"
                onClick={rotate}
                disabled={rotating}
                className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="api-key-rotate-btn"
              >
                {rotating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                Generate new key
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-center text-sm text-[#8d8b8b] py-8">Couldn't load the key</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
